import {
  isSessionIntegrationSource,
  SessionIntegrationSources,
} from "@/lib/checkout/integration-sources";
import { z } from "zod";
import type { NextResponse } from "next/server";

export const INTEGRATION_CONTEXT_HEADER = "x-integration-context";
export const INTEGRATION_CONTEXT_COOKIE = "checkout_integration_context";
export const INTEGRATION_CONTEXT_COOKIE_MAX_AGE = 60 * 60;

const integrationContextWithUrlSchemas = SessionIntegrationSources.map(
  (source) =>
    z.object({
      source: z.literal(source),
      url: z.string().url(),
    }),
);

export const IntegrationContextSchema = z.discriminatedUnion("source", [
  z.object({ source: z.literal("none") }),
  ...integrationContextWithUrlSchemas,
] as [
  z.ZodObject<{ source: z.ZodLiteral<"none"> }>,
  ...typeof integrationContextWithUrlSchemas,
]);

export type IntegrationContext = z.infer<typeof IntegrationContextSchema>;

export const INTEGRATION_CONTEXT_NONE = {
  source: "none",
} as const satisfies Extract<IntegrationContext, { source: "none" }>;

export function hasIntegrationContextUrl(
  context: IntegrationContext,
): context is Exclude<IntegrationContext, { source: "none" }> {
  return context.source !== "none";
}

export function getSessionIntegrationRequestFields(
  context: IntegrationContext,
) {
  if (!hasIntegrationContextUrl(context)) {
    return {};
  }

  return {
    integrationSource: context.source,
    integrationReturnUrl: context.url,
  };
}

export function encodeIntegrationContext(context: IntegrationContext): string {
  return btoa(JSON.stringify(context));
}

export function decodeIntegrationContext(encoded: string): IntegrationContext {
  return IntegrationContextSchema.parse(JSON.parse(atob(encoded)));
}

export function parseIntegrationContextCookieValue(
  value: string,
): IntegrationContext {
  try {
    return decodeIntegrationContext(decodeURIComponent(value));
  } catch {
    return INTEGRATION_CONTEXT_NONE;
  }
}

export function getHotelFunnelReturnUrl(
  context: IntegrationContext,
): string | undefined {
  if (context.source === "none") {
    return undefined;
  }

  return context.url;
}

export function applyIntegrationContextCookie(
  response: NextResponse,
  context: IntegrationContext,
) {
  if (!isSessionIntegrationSource(context.source)) {
    return response;
  }

  response.cookies.set({
    name: INTEGRATION_CONTEXT_COOKIE,
    value: encodeURIComponent(encodeIntegrationContext(context)),
    maxAge: INTEGRATION_CONTEXT_COOKIE_MAX_AGE,
    path: "/",
    sameSite: "lax",
  });

  return response;
}

export function getIntegrationContextFromCookieString(
  cookieHeader: string,
): IntegrationContext {
  const prefix = `${INTEGRATION_CONTEXT_COOKIE}=`;
  const cookie = cookieHeader
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!cookie) {
    return INTEGRATION_CONTEXT_NONE;
  }

  return parseIntegrationContextCookieValue(cookie.slice(prefix.length));
}

export function getHotelFunnelReturnUrlFromDocument(): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  return getHotelFunnelReturnUrl(
    getIntegrationContextFromCookieString(document.cookie),
  );
}

export function getIntegrationContextFromHeaders(
  headers: Headers,
): IntegrationContext {
  const header = headers.get(INTEGRATION_CONTEXT_HEADER);

  if (!header) {
    return INTEGRATION_CONTEXT_NONE;
  }

  return decodeIntegrationContext(header);
}

export function getIntegrationContextFromCookie(
  cookieValue: string | undefined,
): IntegrationContext {
  if (!cookieValue) {
    return INTEGRATION_CONTEXT_NONE;
  }

  return parseIntegrationContextCookieValue(cookieValue);
}

export function resolveIntegrationContext(
  headerContext: IntegrationContext,
  cookieContext: IntegrationContext,
): IntegrationContext {
  if (isSessionIntegrationSource(headerContext.source)) {
    return headerContext;
  }

  return cookieContext;
}

type CookieReader = {
  get: (name: string) => { value: string } | undefined;
};

export function getRequestIntegrationContext(
  headers: Headers,
  cookies: CookieReader,
): IntegrationContext {
  return resolveIntegrationContext(
    getIntegrationContextFromHeaders(headers),
    getIntegrationContextFromCookie(
      cookies.get(INTEGRATION_CONTEXT_COOKIE)?.value,
    ),
  );
}
